// Custom hook for fetching and managing exam data
import { useState, useEffect } from 'react';
import { formatApiData } from '../utils/apiUtils';
import { useLatestTermCode } from './useLatestTermCode';
import { EXAM_SCHEDULE_DATA } from '../data/mockExamSchedule';

// Add this validation function
const validateExamData = (rawData, formattedData) => {
  // Count how many exams we should have (from the original API response)
  let originalExamCount = 0;
  if (rawData && rawData.data) {
    originalExamCount = rawData.data.length;
  }
  
  // Count how many unique exams we ended up with after parsing
  const uniqueCourseEvents = new Set();
  formattedData.forEach(exam => {
    // Create a unique identifier using course + date + time
    const examId = `${exam.course}|${exam.date}|${exam.time}`;
    uniqueCourseEvents.add(examId);
  });
  
  // Remove or comment these lines:
  // console.log('API returned', originalExamCount, 'exams');
  // console.log('After parsing, we have', uniqueCourseEvents.size, 'unique exam events');
  // console.log('Total exam entries (including all locations/sections):', formattedData.length);
  
  // Generate validation warning if the number of unique exams doesn't match original count
  if (uniqueCourseEvents.size !== originalExamCount) {
    console.warn(
      `⚠️ VALIDATION WARNING: Original API had ${originalExamCount} exams, ` +
      `but parsing resulted in ${uniqueCourseEvents.size} unique exams. ` +
      `Some exams may have been lost in parsing.`
    );
    return false;
  }
  
  return true;
};

export const useExamSchedule = () => {
  const [examSchedule, setExamSchedule] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  
  // Get the term code for API requests
  const { termCode, termLabel, isLoading: isLoadingTerm, error: termError } = useLatestTermCode();
  
  useEffect(() => {
    // Only fetch when we have a term code or know we failed to get one
    if (isLoadingTerm) {
      return; // Still waiting for term code to load
    }
    
    // If we don't have a term code, don't attempt to fetch
    if (!termCode) {
      const baseError = termError || "No term code available for API request";
      setError(`Hmm, that's weird. ${baseError}. If this issue persists, please contact me on Telegram: BodyElectric`);
      setIsLoading(false);
      return;
    }
    
    const fetchExamSchedule = async () => {
      setIsLoading(true);
      try {
        // In development, check if we should use the mock data
        const isDev = process.env.NODE_ENV === 'development';
        const useMockData = isDev && (localStorage.getItem('use_mock_data') === 'true');
        
        let formattedData;
        let rawData;
        
        if (useMockData) {
          console.log('Using mock exam schedule data for development');
          rawData = EXAM_SCHEDULE_DATA;
          formattedData = formatApiData(rawData);
        } else {
          // Normal API flow
          // Modify the API URL to point to your local server in development
          const apiUrl = process.env.NODE_ENV === 'development' 
            ? `http://localhost:3001/api/proxy?term_code=${termCode}`
            : `/api/proxy?term_code=${termCode}`;

          const response = await fetch(apiUrl);
          
          if (!response.ok) {
            // In development, fallback to mock data on API failure
            if (isDev) {
              console.warn('API request failed, falling back to mock data');
              rawData = EXAM_SCHEDULE_DATA;
              formattedData = formatApiData(rawData);
            } else {
              throw new Error(`API responded with status: ${response.status}`);
            }
          } else {
            rawData = await response.json();
            formattedData = formatApiData(rawData);
          }
        }
        
        // The rest of your existing code...
        const isValid = validateExamData(rawData, formattedData);
        setValidationResult(isValid);
        
        setExamSchedule(formattedData);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching exam schedule:', err);
        setError(`Unable to fetch exam schedule. ${err.message}`);
        setExamSchedule([]);
        setIsLoading(false);
      }
    };
    
    fetchExamSchedule();
  }, [termCode, isLoadingTerm, termError]);
  
  return { 
    examSchedule, 
    isLoading: isLoading || isLoadingTerm,
    error: error || termError,
    validationResult,
    termLabel // Return the term label for display
  };
};